/**
 * COURSE DETAIL SCREEN
 *
 * Training course content with video player link and quiz CTA.
 */
import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Linking, ActivityIndicator, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { MOCK_COURSES } from '../../data/mockData';

export function CourseScreen({ route, navigation }: any) {
  const { courseId, fromJobId, fromJob } = route.params;
  const [course, setCourse] = useState<any>(null);

  useEffect(() => {
    api.get(`/training/${courseId}`)
      .then(r => setCourse(r.data))
      .catch(() => {
        const mock = MOCK_COURSES.find(c => c.id === courseId);
        if (mock) setCourse(mock);
      });
  }, [courseId]);

  if (!course) return <View style={styles.loadingWrap}><ActivityIndicator size="large" color={RD.ink} /></View>;

  const questionCount = course.quizQuestions?.length ?? course._count?.quizQuestions ?? 0;
  const categoryLabel = (course.categorySlug || '').replace(/-/g, ' ');

  // Derive "What you'll learn" bullets from the course material's "You will learn:" block.
  const learnPoints: string[] = (() => {
    const text: string = course.textContent || '';
    return text
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.startsWith('•') || l.startsWith('-') || l.startsWith('*'))
      .map(l => l.replace(/^[•\-*]\s*/, '').trim())
      .filter(Boolean);
  })();

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle} numberOfLines={1}>{course.title}</Text>
          <Text style={styles.headerSub}>Course · {questionCount}-question quiz</Text>
        </View>
        <View style={{ width: 22 }} />
      </View>

      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {/* Video lesson area (v5) — plays the real course video if present */}
        {course.videoUrl ? (
          <TouchableOpacity
            style={styles.videoLesson}
            onPress={() => Linking.openURL(course.videoUrl)}
            activeOpacity={0.85}
          >
            <View style={styles.videoHatch} />
            <View style={styles.videoCenter}>
              <View style={styles.playCircle}>
                <Ionicons name="play" size={26} color={colors.white} />
              </View>
            </View>
            <View style={styles.videoCaption}>
              <Text style={styles.videoCaptionText} numberOfLines={1}>{course.title} · Watch video</Text>
            </View>
          </TouchableOpacity>
        ) : (
          <View style={styles.videoLesson}>
            <View style={styles.videoHatch} />
            <View style={styles.videoCenter}>
              <View style={[styles.playCircle, { backgroundColor: 'rgba(20,16,12,0.35)' }]}>
                <Ionicons name="document-text-outline" size={24} color={colors.white} />
              </View>
            </View>
            <View style={styles.videoCaption}>
              <Text style={styles.videoCaptionText} numberOfLines={1}>Read the guide below</Text>
            </View>
          </View>
        )}

        {/* Pills */}
        <View style={styles.pillRow}>
          <View style={[styles.pill, { backgroundColor: RD.maroonSoft }]}>
            <Text style={[styles.pillText, { color: RD.maroon }]}>{categoryLabel}</Text>
          </View>
          <View style={[styles.pill, { backgroundColor: RD.amberSoft }]}>
            <Text style={[styles.pillText, { color: RD.amber }]}>Pass: {course.passingScore}%</Text>
          </View>
        </View>

        {/* What you'll learn (v5) */}
        {learnPoints.length > 0 && (
          <View style={styles.contentCard}>
            <Text style={styles.contentTitle}>What you'll learn</Text>
            {learnPoints.map((t, i) => (
              <View key={i} style={styles.learnRow}>
                <Ionicons name="checkmark-circle" size={15} color={RD.money} style={{ marginTop: 2 }} />
                <Text style={styles.learnText}>{t}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Course material */}
        {course.textContent && (
          <View style={styles.contentCard}>
            <Text style={styles.contentTitle}>Course Material</Text>
            <Text style={styles.contentBody}>{course.textContent}</Text>
          </View>
        )}

        {/* Start Quiz CTA (v5) */}
        <TouchableOpacity
          style={styles.quizBtn}
          onPress={() => navigation.navigate('Quiz', { courseId, title: course.title, fromJobId, fromJob })}
          activeOpacity={0.85}
        >
          <Ionicons name="school" size={18} color={colors.white} />
          <Text style={styles.quizBtnText}>Start the Quiz</Text>
        </TouchableOpacity>
        <Text style={styles.quizHint}>{questionCount} questions · {course.passingScore}% to pass · retake anytime</Text>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container:      { flex: 1, backgroundColor: RD.bg },
  loadingWrap:    { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: RD.bg },

  // Header
  header:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.md, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  backBtn:        { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter:   { flex: 1, alignItems: 'center' },
  headerTitle:    { fontSize: 18, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  headerSub:      { fontSize: 12, color: RD.textFaint, marginTop: 2 },

  content:        { padding: spacing.lg, gap: 14, paddingBottom: 40 },

  // Video lesson area (16:9)
  videoLesson:    { borderRadius: 18, overflow: 'hidden', borderWidth: 1, borderColor: RD.g10, aspectRatio: 16 / 9, backgroundColor: RD.g07, justifyContent: 'center', alignItems: 'center' },
  videoHatch:     { ...StyleSheet.absoluteFillObject, backgroundColor: RD.g05 },
  videoCenter:    { ...StyleSheet.absoluteFillObject, justifyContent: 'center', alignItems: 'center' },
  playCircle:     { width: 56, height: 56, borderRadius: 28, backgroundColor: 'rgba(20,16,12,0.55)', justifyContent: 'center', alignItems: 'center' },
  videoCaption:   { position: 'absolute', left: 12, bottom: 10 },
  videoCaptionText:{ color: '#fff', fontSize: 12, fontWeight: '600' },

  // Pills
  pillRow:        { flexDirection: 'row', gap: 7, flexWrap: 'wrap' },
  pill:           { borderRadius: radii.full, paddingHorizontal: 10, paddingVertical: 4 },
  pillText:       { fontSize: 11.5, fontWeight: '700', textTransform: 'capitalize' },

  // Content
  contentCard:    { backgroundColor: RD.surface, borderRadius: 18, padding: 16, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  contentTitle:   { fontSize: 15, fontWeight: '700', color: RD.text, marginBottom: spacing.sm, fontFamily: fonts.semiBold },
  contentBody:    { fontSize: 14.5, color: RD.textSec, lineHeight: 23 },

  // What you'll learn rows
  learnRow:       { flexDirection: 'row', gap: 9, alignItems: 'flex-start', paddingVertical: 6 },
  learnText:      { flex: 1, fontSize: 13.5, color: RD.textSec, lineHeight: 19 },

  // Start Quiz CTA
  quizBtn:        { backgroundColor: RD.ink, borderRadius: 16, height: 54, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8 },
  quizBtnText:    { fontSize: 16, fontWeight: '600', color: '#fff', fontFamily: fonts.semiBold },
  quizHint:       { fontSize: 12, color: RD.textFaint, textAlign: 'center', marginTop: -4 },
});
